import { Checkbox, type CheckboxProps, Paper, Typography } from '@mui/material';
import type { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import type { BillingCycleInvoice } from '@vy/api-definition';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { extract, isEmptyArray, isMultiple, purge, vyDate, withDefault } from '@vy/utils';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { ApprovedIcon } from '../../../components/icons/ApprovedIcon';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { VyTable } from '../../../components/new-design/table/vy-table/VyTable';
import type { VyTableMinimalProps } from '../../../components/new-design/table/vy-table/vy-table-types';
import { useUpdateInvoicesReviewStatuses } from '../../../hooks/requests/use-update-invoices-review-statuses';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { runMutation } from '../../../utils/vy-mutation';
import { useQueryInputParams } from '../../invoices/hooks/use-query-input-param';
import { BILLING_CYCLE_COLUMNS } from './billing-cycle-columns';
import { ExportBillingCycleToCsv } from './ExportToCsv';

type BillingCycleTableProps = VyTableMinimalProps<BillingCycleInvoice>;

export const BillingCycleTable: React.FC<BillingCycleTableProps> = (props) => {
  const navigate = useVyNavigate();
  const [selectedInvoicesRows, setSelectedInvoicesRows] = useState<BillingCycleInvoice[]>([]);
  const [query, setQuery] = useQueryInputParams();

  const queryClient = useQueryClient();
  const updateInvoicesReviewStatusesMutation = useUpdateInvoicesReviewStatuses();

  const handleApproveInvoices = useCallback(async () => {
    const invoiceIds = extract(selectedInvoicesRows, 'id');
    const invoiceCustomerIds = extract(selectedInvoicesRows, 'customerId');
    const invoicesText = isMultiple(invoiceIds) ? 'invoices' : 'invoice';

    await runMutation(
      updateInvoicesReviewStatusesMutation,
      {
        customerIds: invoiceCustomerIds,
        invoicesByNewStatus: { [INVOICE_REVIEW_STATUSES.Approved]: invoiceIds },
      },
      {
        pending: `Approving ${invoicesText}...`,
        success: `Approved ${invoicesText}`,
        error: `Failed to approve ${invoicesText}`,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              ['billing-cycle-page', 'billing-cycle-invoices'].some((key) => queryKey.includes(key)),
          });
          setSelectedInvoicesRows([]);
        },
      },
    );
  }, [selectedInvoicesRows, updateInvoicesReviewStatusesMutation]);

  const canApproveInvoice =
    isEmptyArray(selectedInvoicesRows) || updateInvoicesReviewStatusesMutation.isLoading;

  const handleRowSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
    const newSelectedRows = Array.from(selectionModel.ids).map((id) => props.items.find((item) => item.id === id));
    const purgedSelectedRows = purge(newSelectedRows);

    setSelectedInvoicesRows(purgedSelectedRows);
  };

  return (
    <Paper
      sx={(theme) => ({
        borderRadius: '8px',
        border: '1px solid',
        borderColor: theme.palette.divider,
        height: '100%',
        position: 'relative',
      })}
    >
      <VyTableToolbar>
        <VyTableToolbar.Queries>
          <VyTableToolbar.SearchInput
            defaultValue={withDefault(query, '')}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search here"
          />
        </VyTableToolbar.Queries>

        <VyTableToolbar.Actions>
          <ExportBillingCycleToCsv
            endDay={vyDate().endOf('month').toDate()}
            startDay={vyDate().startOf('month').toDate()}
          />

          <VyTableToolbar.CtaButton onClick={handleApproveInvoices} disabled={canApproveInvoice}>
            Approve
          </VyTableToolbar.CtaButton>
        </VyTableToolbar.Actions>
      </VyTableToolbar>

      <VyTable
        {...props}
        columns={BILLING_CYCLE_COLUMNS}
        sx={{
          paddingBottom: '180px',
          marginBottom: '200px',
          ...props.sx,
        }}
        paginationClassName="bottom-0 right-0"
        rowSelection
        checkboxSelection
        onRowClick={({ row }: GridRowParams<BillingCycleInvoice>) =>
          row.reviewStatus === INVOICE_REVIEW_STATUSES.Approved
            ? navigate(`/invoices/overview/light/${row.id}`)
            : navigate(`/invoices/monthly-approval?invoiceId=${row.id}`)
        }
        onRowSelectionModelChange={handleRowSelectionModelChange}
        isRowSelectable={({ row }: GridRowParams<BillingCycleInvoice>) =>
          row.reviewStatus !== INVOICE_REVIEW_STATUSES.Approved
        }
        slots={{
          baseCheckbox: (checkboxProps) => {
            if (checkboxProps.disabled) {
              return (
                <div className="flex items-center justify-center">
                  <ApprovedIcon className="h-6 w-6" />
                </div>
              );
            }

            return <Checkbox {...(checkboxProps as unknown as CheckboxProps)} />;
          },
        }}
      />

      <Typography
        variant="body1"
        sx={{
          position: 'absolute',
          bottom: '16px',
          left: '20px',
        }}
      >
        {`${selectedInvoicesRows.length} Invoices Selected`}
      </Typography>
    </Paper>
  );
};
